import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  GET_QUESTION_SECTIONS,
  GetQuestionSectionsQuery,
  GetQuestionSectionsQueryVariables,
  useCurrentCompanyId,
  Question,
  QuestionSection,
  QuestionSectionCode,
  QuestionCode,
} from '@neo.tax/shared';

import { useRdContext } from '../context';

type UseQuestionSections = () => {
  loading: boolean;
  questionSections: QuestionSection[];
  taxYear: number | null;
  getQuestion: (
    questionSections: QuestionSection[],
    sectionCode: QuestionSectionCode,
    questionCode: QuestionCode,
  ) => Question | undefined;
  getAnswer: (
    questionSections: QuestionSection[],
    sectionCode: QuestionSectionCode,
    questionCode: QuestionCode,
  ) => string | null | undefined;
};

export const useQuestionSections: UseQuestionSections = () => {
  const companyId = useCurrentCompanyId();
  const { fiscalYear } = useRdContext();

  const { data, error, loading } = useQuery<GetQuestionSectionsQuery, GetQuestionSectionsQueryVariables>(
    GET_QUESTION_SECTIONS,
    {
      variables: {
        companyId,
      },
    },
  );

  const getQuestion = (
    questionSections: QuestionSection[],
    sectionCode: QuestionSectionCode,
    questionCode: QuestionCode,
  ): Question | undefined =>
    questionSections
      .find((a) => a.sectionCode === sectionCode)
      ?.questions?.find((q) => q.questionCode === questionCode);

  const getAnswer = (
    questionSections: QuestionSection[],
    sectionCode: QuestionSectionCode,
    questionCode: QuestionCode,
  ): string | null | undefined => getQuestion(questionSections, sectionCode, questionCode)?.answerValue;

  if (loading) {
    return {
      questionSections: [],
      taxYear: null,
      loading: true,
      getQuestion,
      getAnswer,
    };
  }
  if (error) {
    throw error;
  }
  if (!data) {
    throw createError('MissingQuestionSections', 'Failed to load question sections');
  }
  return {
    questionSections: data.getCompany.questionSections,
    taxYear: fiscalYear.year,
    loading,
    getQuestion,
    getAnswer,
  };
};
